import { useState, useCallback, useRef, useEffect, useMemo } from 'react';
import { PIECE_CONFIG } from '../lib/shapes';
import {
  CANVAS_W, CANVAS_H, MIN_DIST, PIECE_R,
  ZONE_POROUS, ZONE_UNCERTAIN, ZONE_NERVOUS,
  EQUILIBRIUM_THRESHOLD_MS, UNKNOWN_CREEP_START_MS, UNKNOWN_CREEP_RATE,
  IDLE_CREEP_DIVISOR, IDLE_PRESSURE_START_MS,
  VOID_FORCE_RADIUS, VOID_PENALTY, LINE_ERASE_RADIUS, SUPER_SLOW_THRESHOLD,
  SPECIAL_SHAPES
} from '../lib/gameConstants';
import {
  dist, getCentroid, calcDensity, calcSymmetry, calcLinearity, calcRegularity,
  patternConfidence, historySimilarity, getAllPoints
} from '../lib/patternAnalysis';
import {
  CHAOS_EFFECTS, CHAOS_NAMES, RADICAL_EFFECTS, RADICAL_NAMES, applyLineEffects
} from '../lib/forceUnknownEffects';

// Re-export for components that import from here
export { CANVAS_W, CANVAS_H };

// --- Game Engine Hook ---
export function useGameEngine(learningProfile, isPaused = false) {
  // Core state
  const [pieces, setPieces] = useState([]);
  const [confidence, setConfidence] = useState(0);
  const [selectedPiece, setSelectedPiece] = useState('cube');
  const [selectedColor, setSelectedColor] = useState('black');
  const [colorPreferences, setColorPreferences] = useState({});
  const [phase, setPhase] = useState('idle');
  const [roundsInSession, setRoundsInSession] = useState(0);
  const [totalPiecesPlaced, setTotalPiecesPlaced] = useState(0);
  const [fundamentalForm, setFundamentalForm] = useState([]);
  const [destroyingPieces, setDestroyingPieces] = useState([]);
  const [forcedUnknownCooldown, setForcedUnknownCooldown] = useState(false);
  const [maxConfidence, setMaxConfidence] = useState(0);
  const [lastChaosEffect, setLastChaosEffect] = useState('');
  
  // Drawing state
  const [drawnLines, setDrawnLines] = useState([]);
  const [currentDrawing, setCurrentDrawing] = useState(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [drawingMetrics, setDrawingMetrics] = useState({ speed: 0, curvature: 0, confidence: 0 });
  const lastDrawPointRef = useRef({ x: 0, y: 0, time: 0 });
  
  // Eraser state
  const [isErasing, setIsErasing] = useState(false);
  const [erasedDistance, setErasedDistance] = useState(0);
  
  // Equilibrium & Atmospheric State
  const [atmosphericZone, setAtmosphericZone] = useState('uncertain');
  const [equilibriumReached, setEquilibriumReached] = useState(false);
  const [equilibriumFading, setEquilibriumFading] = useState(false);
  const [timeInPorousZone, setTimeInPorousZone] = useState(0);
  const [measurementStopped, setMeasurementStopped] = useState(false);
  
  // Pressure state
  const [unknownPressure, setUnknownPressure] = useState(0);
  const [gameStartTime, setGameStartTime] = useState(null);
  const [idleTime, setIdleTime] = useState(0);
  const [idleInducedPressure, setIdleInducedPressure] = useState(0);
  const [voidPermanentPenalty, setVoidPermanentPenalty] = useState(0);
  const [unknownDroppingFast, setUnknownDroppingFast] = useState(false);
  
  // Lottery state
  const [lotteryActive, setLotteryActive] = useState(false);
  const [lotteryCurrentShape, setLotteryCurrentShape] = useState(null);
  const [lotteryWonShape, setLotteryWonShape] = useState(null);
  const [lotteryWonCount, setLotteryWonCount] = useState(0);
  const [wonShapesAvailable, setWonShapesAvailable] = useState({});
  const [showEquilibriumPopup, setShowEquilibriumPopup] = useState(false);

  // Refs
  const startTimeRef = useRef(Date.now());
  const piecesRef = useRef([]);
  const learningRef = useRef(null);
  const porousZoneStartRef = useRef(null);
  const entropyTimerRef = useRef(null);
  const unknownTimerRef = useRef(null);
  const lastActivityRef = useRef(Date.now());
  const idleTimerRef = useRef(null);
  const lotteryTimerRef = useRef(null);
  const drawnLinesRef = useRef([]);

  // Sync refs
  useEffect(() => { piecesRef.current = pieces; }, [pieces]);
  useEffect(() => { drawnLinesRef.current = drawnLines; }, [drawnLines]);
  useEffect(() => { learningRef.current = learningProfile; }, [learningProfile]);
  useEffect(() => { if (confidence > maxConfidence && !equilibriumReached) setMaxConfidence(confidence); }, [confidence, maxConfidence, equilibriumReached]);
  
  // --- Selection Handlers ---
  const handleSelectPiece = useCallback((pieceType) => {
    setSelectedPiece(pieceType);
    if (colorPreferences[pieceType]) setSelectedColor(colorPreferences[pieceType]);
  }, [colorPreferences]);
  
  const handleSelectColor = useCallback((colorId) => {
    setSelectedColor(colorId);
    setColorPreferences(prev => ({ ...prev, [selectedPiece]: colorId }));
  }, [selectedPiece]);

  // --- Pressure Timers ---
  useEffect(() => {
    if (phase !== 'building' || equilibriumReached || !gameStartTime || isPaused) {
      if (phase !== 'building' || equilibriumReached || !gameStartTime) setUnknownPressure(0);
      return;
    }
    
    unknownTimerRef.current = setInterval(() => {
      const timeSinceStart = Date.now() - gameStartTime;
      const inPorousZone = confidence >= ZONE_POROUS.min && confidence <= ZONE_POROUS.max;
      const difficultyMult = 1 + (learningRef.current?.difficulty_modifier || 0) * 0.5;
      
      if (inPorousZone) {
        setUnknownPressure(prev => Math.max(0, prev - 1));
      } else if (timeSinceStart > UNKNOWN_CREEP_START_MS) {
        setUnknownPressure(prev => {
          const newPressure = prev + (UNKNOWN_CREEP_RATE * difficultyMult);
          if (confidence + newPressure + idleInducedPressure >= 100) setConfidence(100);
          return Math.min(100 - confidence - idleInducedPressure, newPressure);
        });
      }
    }, 1000);
    
    return () => clearInterval(unknownTimerRef.current);
  }, [phase, equilibriumReached, confidence, gameStartTime, idleInducedPressure, isPaused]);
  
  // Idle pressure timer
  useEffect(() => {
    if (phase !== 'building' || equilibriumReached || isPaused) {
      if (phase !== 'building' || equilibriumReached) { setIdleTime(0); setIdleInducedPressure(0); }
      return;
    }
    
    idleTimerRef.current = setInterval(() => {
      const idle = Date.now() - lastActivityRef.current;
      setIdleTime(idle);
      
      if (idle > IDLE_PRESSURE_START_MS) {
        const idleSeconds = (idle - IDLE_PRESSURE_START_MS) / 1000;
        const difficultyMult = 1 + (learningRef.current?.difficulty_modifier || 0) * 1.0;
        const acceleratedPressure = Math.min(60, (idleSeconds * idleSeconds) / IDLE_CREEP_DIVISOR * difficultyMult);
        setIdleInducedPressure(acceleratedPressure);
      }
    }, 1000);
    
    return () => clearInterval(idleTimerRef.current);
  }, [phase, equilibriumReached, isPaused]);
  
  // Fast unknown creep after equilibrium continue
  useEffect(() => {
    if (!unknownDroppingFast || phase !== 'building' || isPaused) return;
    
    const creepInterval = setInterval(() => {
      setUnknownPressure(prev => {
        const newPressure = prev + 2;
        if (confidence + newPressure + idleInducedPressure >= 100) setConfidence(100);
        return Math.min(100 - confidence - idleInducedPressure, newPressure);
      });
    }, 200);
    
    return () => clearInterval(creepInterval);
  }, [unknownDroppingFast, phase, confidence, idleInducedPressure, isPaused]);

  // --- Helper Functions ---
  const resetIdleTimer = useCallback(() => {
    lastActivityRef.current = Date.now();
    setIdleTime(0);
    setIdleInducedPressure(0);
  }, []);
  
  const stopFastUnknownCreep = useCallback(() => setUnknownDroppingFast(false), []);
  
  const markGameStart = useCallback(() => {
    if (!gameStartTime) setGameStartTime(Date.now());
  }, [gameStartTime]);

  // --- Atmospheric Zone Detection ---
  useEffect(() => {
    if (equilibriumReached) { setAtmosphericZone('equilibrium'); return; }
    
    if (confidence < ZONE_UNCERTAIN.max) {
      setAtmosphericZone('uncertain');
      porousZoneStartRef.current = null;
    } else if (confidence >= ZONE_POROUS.min && confidence <= ZONE_POROUS.max) {
      setAtmosphericZone('porous');
      if (!porousZoneStartRef.current) porousZoneStartRef.current = Date.now();
    } else if (confidence > ZONE_NERVOUS.min) {
      setAtmosphericZone('nervous');
      porousZoneStartRef.current = null;
    } else {
      setAtmosphericZone('transition');
      porousZoneStartRef.current = null;
    }
  }, [confidence, equilibriumReached]);

  // --- Equilibrium Check ---
  useEffect(() => {
    if (equilibriumReached || phase !== 'building' || isPaused) return;
    
    const checkEquilibrium = setInterval(() => {
      if (porousZoneStartRef.current) {
        const elapsed = Date.now() - porousZoneStartRef.current;
        setTimeInPorousZone(elapsed);
        
        const difficultyMult = 1 + (learningRef.current?.difficulty_modifier || 0) * 0.2;
        const adjustedThreshold = EQUILIBRIUM_THRESHOLD_MS * difficultyMult;
        
        if (elapsed >= adjustedThreshold) {
          setEquilibriumReached(true);
          setMeasurementStopped(true);
          setForcedUnknownCooldown(true);
          porousZoneStartRef.current = null;
          
          // Start lottery
          setLotteryActive(true);
          let lotteryTick = 0;
          const totalTicks = 30 + Math.floor(Math.random() * 20);
          lotteryTimerRef.current = setInterval(() => {
            const randomShape = SPECIAL_SHAPES[Math.floor(Math.random() * SPECIAL_SHAPES.length)];
            setLotteryCurrentShape(randomShape);
            lotteryTick++;
            
            if (lotteryTick >= totalTicks) {
              clearInterval(lotteryTimerRef.current);
              const wonShape = SPECIAL_SHAPES[Math.floor(Math.random() * SPECIAL_SHAPES.length)];
              setLotteryCurrentShape(wonShape);
              setLotteryWonShape(wonShape);
              
              const wonCount = 1 + Math.floor(Math.random() * 3);
              setLotteryWonCount(wonCount);
              setWonShapesAvailable(prev => ({ ...prev, [wonShape]: (prev[wonShape] || 0) + wonCount }));
              
              setTimeout(() => { setLotteryActive(false); setShowEquilibriumPopup(true); }, 2000);
            }
          }, 150 + lotteryTick * 5);
        }
      }
    }, 500);
    
    return () => clearInterval(checkEquilibrium);
  }, [equilibriumReached, phase, isPaused]);

  // Entropy creep during fading
  useEffect(() => {
    if (!equilibriumFading || phase !== 'building' || isPaused) return;
    setMeasurementStopped(false);
    
    const entropyCreep = setInterval(() => {
      setConfidence(prev => {
        const newConf = prev + 0.5;
        if (newConf >= 100) clearInterval(entropyCreep);
        return Math.min(100, newConf);
      });
    }, 2000);
    
    return () => clearInterval(entropyCreep);
  }, [equilibriumFading, phase, isPaused]);

  // --- Confidence Calculation ---
  const getDynamicConfidence = useCallback((pieceType, currentConf) => {
    const zone = currentConf < 40 ? 'blue' : currentConf < 60 ? 'orange' : 'greenred';
    const dynamicValues = {
      cube: { blue: 2, orange: 3, greenred: 4 },
      sphere: { blue: 1, orange: 2, greenred: 3 },
      pyramid: { blue: 3, orange: 4, greenred: 5 },
      hexagon: { blue: 1, orange: 2, greenred: 3 },
      diamond: { blue: 1, orange: 1, greenred: 1 },
      star: { blue: 4, orange: 5, greenred: 6 },
      void: { blue: -2, orange: -2, greenred: -2 },
    };
    
    if (SPECIAL_SHAPES.includes(pieceType)) return -2;
    return dynamicValues[pieceType]?.[zone] ?? 2;
  }, []);

  const recalcConfidence = useCallback((currentPieces, checkVoidReversal = false) => {
    if (measurementStopped && !equilibriumFading) return;
    if (currentPieces.length === 0) { setConfidence(0); return; }
    
    let runningConf = 0;
    for (const p of currentPieces) {
      const pieceValue = getDynamicConfidence(p.type, runningConf);
      runningConf = Math.max(0, runningConf + pieceValue * 0.8);
    }
    
    const pattern = patternConfidence(currentPieces, drawnLinesRef.current) * 20;
    const history = historySimilarity(currentPieces, learningRef.current) * 10;
    const diff = 1 + (learningRef.current?.difficulty_modifier || 0) * 0.3;
    
    let raw = (runningConf + pattern + history) * diff;
    
    if (raw > 70) raw = 70 + (raw - 70) * 1.4;
    else if (raw > 50) raw = 50 + (raw - 50) * 1.1;
    
    const newConfidence = Math.min(100, Math.max(0, raw));
    
    if (checkVoidReversal && voidPermanentPenalty > 0) {
      setConfidence(prev => {
        const increase = Math.round(newConfidence - prev);
        if (increase >= 1 && increase <= 3) {
          setVoidPermanentPenalty(p => Math.max(0, p - increase));
        }
        return newConfidence;
      });
    } else {
      setConfidence(newConfidence);
    }
  }, [measurementStopped, equilibriumFading, getDynamicConfidence, voidPermanentPenalty]);

  // --- Destruction ---
  const triggerDestruction = useCallback(() => {
    const cur = piecesRef.current;
    const diff = learningRef.current?.difficulty_modifier || 0;
    setPhase('destruction');

    const scored = cur.map(p => {
      const neighbors = cur.filter(q => q.id !== p.id && dist(p, q) < 90).length;
      const bonus = p.type === 'void' ? 10 : 0;
      return { ...p, stability: neighbors + bonus };
    }).sort((a, b) => b.stability - a.stability);

    const survivalRate = Math.max(0.1, 0.3 - diff * 0.03);
    const survivalCount = Math.max(1, Math.floor(cur.length * survivalRate));
    const survivors = scored.slice(0, survivalCount).map(p => ({ ...p, isRemnant: true }));
    const destroyed = scored.slice(survivalCount).map(p => ({
      ...p, exitX: (Math.random() - 0.5) * 600, exitY: (Math.random() - 0.5) * 600,
    }));

    setDestroyingPieces(destroyed);
    
    // Line remnants
    const currentLines = drawnLinesRef.current;
    const lineSurvivalRate = Math.max(0.15, 0.35 - diff * 0.05);
    const lineSurvivalCount = Math.max(0, Math.floor(currentLines.length * lineSurvivalRate));
    const sortedLines = [...currentLines].sort((a, b) => (b.points?.length || 0) - (a.points?.length || 0));
    const survivingLines = sortedLines.slice(0, lineSurvivalCount).map(line => ({ ...line, isRemnant: true, opacity: 0.6 }));
    const destroyingLines = sortedLines.slice(lineSurvivalCount);
    
    setDrawnLines(prev => prev.map(line => ({
      ...line, destroying: destroyingLines.some(dl => dl.id === line.id),
    })));
    
    setTimeout(() => {
      setPieces(survivors);
      setFundamentalForm(survivors);
      recalcConfidence(survivors);
      setDestroyingPieces([]);
      setDrawnLines(survivingLines);
      setRoundsInSession(prev => prev + 1);
      setPhase('building');
    }, 2200);
  }, [recalcConfidence]);

  // Trigger destruction at effective max
  useEffect(() => {
    const effectiveMax = 100 - unknownPressure - idleInducedPressure - voidPermanentPenalty;
    if (confidence >= effectiveMax && phase === 'building' && effectiveMax > 0) {
      triggerDestruction();
    }
  }, [confidence, phase, triggerDestruction, unknownPressure, idleInducedPressure, voidPermanentPenalty]);

  // --- Line/Eraser Functions ---
  const getNearestLineInfo = useCallback((x, y) => {
    let nearestLine = null;
    let minDist = Infinity;
    
    for (const line of drawnLinesRef.current) {
      if (!line.points || line.points.length < 2) continue;
      for (let i = 0; i < line.points.length - 1; i++) {
        const p1 = line.points[i], p2 = line.points[i + 1];
        const dx = p2.x - p1.x, dy = p2.y - p1.y;
        const len2 = dx * dx + dy * dy;
        let t = len2 > 0 ? Math.max(0, Math.min(1, ((x - p1.x) * dx + (y - p1.y) * dy) / len2)) : 0;
        const closestX = p1.x + t * dx, closestY = p1.y + t * dy;
        const d = Math.sqrt((x - closestX) ** 2 + (y - closestY) ** 2);
        if (d < minDist) { minDist = d; nearestLine = line; }
      }
    }
    
    return { near: minDist < LINE_ERASE_RADIUS, line: minDist < LINE_ERASE_RADIUS ? nearestLine : null, distance: minDist };
  }, []);

  const isNearLine = useCallback((x, y) => getNearestLineInfo(x, y).near, [getNearestLineInfo]);

  const getTopmostAtPosition = useCallback((x, y) => {
    const piecesAtPos = piecesRef.current.filter(p => dist(p, { x, y }) < MIN_DIST).sort((a, b) => (b.placedAt || 0) - (a.placedAt || 0));
    const topPiece = piecesAtPos[0] || null;
    const lineInfo = getNearestLineInfo(x, y);
    const topLine = lineInfo.line;
    
    if (!topPiece && !topLine) return { type: 'none', element: null };
    if (topPiece && !topLine) return { type: 'piece', element: topPiece };
    if (!topPiece && topLine) return { type: 'line', element: topLine };
    
    return (topPiece.placedAt || 0) >= (topLine.placedAt || 0)
      ? { type: 'piece', element: topPiece }
      : { type: 'line', element: topLine };
  }, [getNearestLineInfo]);

  const eraseAtPoint = useCallback((x, y) => {
    let totalErased = 0;
    
    setDrawnLines(prev => {
      const newLines = [];
      for (const line of prev) {
        if (!line.points || line.points.length < 2) { newLines.push(line); continue; }
        
        const segments = [];
        let currentSegment = [];
        
        for (let i = 0; i < line.points.length; i++) {
          const pt = line.points[i];
          const d = Math.sqrt((pt.x - x) ** 2 + (pt.y - y) ** 2);
          
          if (d > LINE_ERASE_RADIUS) {
            currentSegment.push(pt);
          } else {
            if (i > 0) {
              const prevPt = line.points[i - 1];
              totalErased += Math.sqrt((pt.x - prevPt.x) ** 2 + (pt.y - prevPt.y) ** 2);
            }
            if (currentSegment.length >= 2) segments.push(currentSegment);
            currentSegment = [];
          }
        }
        if (currentSegment.length >= 2) segments.push(currentSegment);
        
        segments.forEach((seg, idx) => {
          newLines.push({ ...line, id: `${line.id}_seg${idx}_${Date.now()}`, points: seg });
        });
      }
      return newLines;
    });
    
    if (totalErased > 0) {
      setErasedDistance(prev => prev + totalErased);
      setConfidence(prev => Math.max(0, prev - Math.min(0.5, totalErased / 30)));
    }
  }, []);

  const startErasing = useCallback((x, y) => {
    if (phase !== 'building' || selectedPiece !== 'void' || isPaused) return false;
    const topmost = getTopmostAtPosition(x, y);
    if (topmost.type === 'line') {
      setIsErasing(true);
      setErasedDistance(0);
      markGameStart();
      eraseAtPoint(x, y);
      return true;
    }
    return false;
  }, [phase, selectedPiece, isPaused, getTopmostAtPosition, markGameStart, eraseAtPoint]);

  const continueErasing = useCallback((x, y) => { if (isErasing) eraseAtPoint(x, y); }, [isErasing, eraseAtPoint]);

  const endErasing = useCallback(() => {
    if (!isErasing) return;
    const distanceFactor = Math.min(3, erasedDistance / 100);
    const finalPenalty = Math.round(VOID_PENALTY * (0.3 + distanceFactor * 0.7));
    if (erasedDistance > 5) setVoidPermanentPenalty(prev => prev + finalPenalty);
    setIsErasing(false);
    setErasedDistance(0);
    resetIdleTimer();
  }, [isErasing, erasedDistance, resetIdleTimer]);

  // --- Piece Placement ---
  const placePiece = useCallback((x, y) => {
    if (phase !== 'building' || isPaused) return false;
    
    const clamped = {
      x: Math.max(PIECE_R, Math.min(CANVAS_W - PIECE_R, x)),
      y: Math.max(PIECE_R, Math.min(CANVAS_H - PIECE_R, y)),
    };
    
    // Void block behavior
    if (selectedPiece === 'void') {
      const piecesAtPosition = piecesRef.current.filter(p => dist(p, clamped) < MIN_DIST).sort((a, b) => b.placedAt - a.placedAt);
      const pieceToErase = piecesAtPosition[0];
      
      if (pieceToErase) {
        const newPieces = piecesRef.current.filter(p => p.id !== pieceToErase.id);
        setPieces(newPieces);
        recalcConfidence(newPieces);
        setVoidPermanentPenalty(prev => prev + VOID_PENALTY);
        markGameStart();
        resetIdleTimer();
        return true;
      } else {
        setVoidPermanentPenalty(prev => prev + VOID_PENALTY);
        
        const piecesInRange = piecesRef.current.filter(p => dist(p, clamped) < VOID_FORCE_RADIUS);
        if (piecesInRange.length > 0) {
          const affectedPieces = piecesRef.current.map(p => {
            if (dist(p, clamped) < VOID_FORCE_RADIUS) {
              if (p.type === 'void') {
                const angle = Math.atan2(clamped.y - p.y, clamped.x - p.x);
                const pullDist = Math.min(dist(p, clamped) * 0.6, 40);
                return { ...p, x: Math.max(PIECE_R, Math.min(CANVAS_W - PIECE_R, p.x + Math.cos(angle) * pullDist)), y: Math.max(PIECE_R, Math.min(CANVAS_H - PIECE_R, p.y + Math.sin(angle) * pullDist)) };
              } else {
                const angle = Math.atan2(p.y - clamped.y, p.x - clamped.x);
                const pushDist = 20 + Math.random() * 30;
                return { ...p, x: Math.max(PIECE_R, Math.min(CANVAS_W - PIECE_R, p.x + Math.cos(angle) * pushDist)), y: Math.max(PIECE_R, Math.min(CANVAS_H - PIECE_R, p.y + Math.sin(angle) * pushDist)) };
              }
            }
            return p;
          });
          setPieces(affectedPieces);
          recalcConfidence(affectedPieces);
        }
        
        const newPiece = { id: `p_${Date.now()}_${Math.random().toString(36).substr(2, 6)}`, type: 'void', color: selectedColor, x: clamped.x, y: clamped.y, placedAt: Date.now(), isRemnant: false };
        setPieces(prev => [...prev, newPiece]);
        setTotalPiecesPlaced(prev => prev + 1);
        markGameStart();
        resetIdleTimer();
        return true;
      }
    }
    
    // Special shape check
    const isSpecialShape = SPECIAL_SHAPES.includes(selectedPiece);
    if (isSpecialShape && (wonShapesAvailable[selectedPiece] || 0) <= 0) return false;
    
    if (isSpecialShape) {
      setWonShapesAvailable(prev => ({ ...prev, [selectedPiece]: Math.max(0, (prev[selectedPiece] || 0) - 1) }));
    }

    markGameStart();
    resetIdleTimer();
    stopFastUnknownCreep();

    const newPiece = { id: `p_${Date.now()}_${Math.random().toString(36).substr(2, 6)}`, type: selectedPiece, color: selectedColor, x: clamped.x, y: clamped.y, placedAt: Date.now(), isRemnant: false };
    const newPieces = [...piecesRef.current, newPiece];
    setPieces(newPieces);
    setTotalPiecesPlaced(prev => prev + 1);
    
    const inGreenZone = confidence >= 60 && confidence <= 85;
    if (isSpecialShape && inGreenZone) {
      recalcConfidence(newPieces, selectedPiece !== 'void');
      setConfidence(prev => Math.max(60, prev));
    } else {
      recalcConfidence(newPieces, selectedPiece !== 'void');
    }
    
    return true;
  }, [phase, selectedPiece, selectedColor, confidence, recalcConfidence, markGameStart, resetIdleTimer, stopFastUnknownCreep, wonShapesAvailable, isPaused]);

  // --- Drawing Functions ---
  const calculateStraightness = useCallback((points) => {
    if (points.length < 3) return 1;
    let totalDist = 0;
    for (let i = 1; i < points.length; i++) {
      totalDist += Math.sqrt((points[i].x - points[i-1].x) ** 2 + (points[i].y - points[i-1].y) ** 2);
    }
    const straightDist = Math.sqrt((points[points.length-1].x - points[0].x) ** 2 + (points[points.length-1].y - points[0].y) ** 2);
    return totalDist === 0 ? 0 : Math.min(1, straightDist / totalDist);
  }, []);

  const rotatePiece = useCallback((pieceId) => {
    if (phase !== 'building' || isPaused) return false;
    setPieces(prev => prev.map(p => p.id === pieceId && !p.isRemnant ? { ...p, rotation: ((p.rotation || 0) + 45) % 360 } : p));
    resetIdleTimer();
    return true;
  }, [phase, isPaused, resetIdleTimer]);

  const findPieceAtPoint = useCallback((x, y) => {
    for (const piece of piecesRef.current) {
      if (piece.isRemnant) continue;
      if (Math.sqrt((piece.x - x) ** 2 + (piece.y - y) ** 2) < 25) return piece;
    }
    return null;
  }, []);

  const handleRotateTool = useCallback((x, y) => {
    if (phase !== 'building' || selectedPiece !== 'rotate' || isPaused) return false;
    const clickedPiece = findPieceAtPoint(x, y);
    if (clickedPiece) { rotatePiece(clickedPiece.id); markGameStart(); resetIdleTimer(); return true; }
    return false;
  }, [phase, selectedPiece, isPaused, findPieceAtPoint, rotatePiece, markGameStart, resetIdleTimer]);

  const startDrawing = useCallback((x, y) => {
    if (phase !== 'building' || selectedPiece !== 'pen' || isPaused) return 'none';
    markGameStart();
    setIsDrawing(true);
    setDrawingMetrics({ speed: 0, curvature: 0, confidence: 0 });
    lastDrawPointRef.current = { x, y, time: Date.now() };
    setCurrentDrawing({ id: `line_${Date.now()}`, points: [{ x, y }], color: selectedColor });
    return 'draw';
  }, [phase, selectedPiece, selectedColor, isPaused, markGameStart]);

  const continueDrawing = useCallback((x, y) => {
    if (!isDrawing || !currentDrawing) return;
    
    const now = Date.now();
    const lastPoint = lastDrawPointRef.current;
    const segmentDist = Math.sqrt((x - lastPoint.x) ** 2 + (y - lastPoint.y) ** 2);
    if (segmentDist < 2) return;
    
    const timeDelta = Math.max(1, now - lastPoint.time);
    const currentSpeed = (segmentDist / timeDelta) * 1000;
    const newPoints = [...currentDrawing.points, { x, y }];
    
    let curvature = 0;
    if (newPoints.length >= 3) {
      let totalDist = 0;
      for (let i = 1; i < newPoints.length; i++) totalDist += Math.sqrt((newPoints[i].x - newPoints[i-1].x) ** 2 + (newPoints[i].y - newPoints[i-1].y) ** 2);
      const straightDist = Math.sqrt((newPoints[newPoints.length-1].x - newPoints[0].x) ** 2 + (newPoints[newPoints.length-1].y - newPoints[0].y) ** 2);
      if (totalDist > 0) curvature = 1 - Math.min(1, straightDist / totalDist);
    }
    
    if (currentSpeed < SUPER_SLOW_THRESHOLD) {
      const slowPenalty = 0.15 + (1 - currentSpeed / SUPER_SLOW_THRESHOLD) * 0.2;
      setConfidence(prev => Math.max(0, prev - slowPenalty));
      if (newPoints.length % 10 === 0) setVoidPermanentPenalty(prev => prev + 0.5);
      setDrawingMetrics(prev => ({ speed: prev.speed * 0.6 + currentSpeed * 0.4, curvature: curvature * 100, confidence: -slowPenalty }));
    } else {
      const speedBonus = Math.min(1, currentSpeed / 400);
      const segmentConfidence = 0.02 + ((1 - curvature) * 0.05) + (speedBonus * 0.03);
      setConfidence(prev => Math.min(100, prev + segmentConfidence));
      setDrawingMetrics(prev => ({ speed: prev.speed * 0.6 + currentSpeed * 0.4, curvature: curvature * 100, confidence: segmentConfidence }));
    }
    
    lastDrawPointRef.current = { x, y, time: now };
    setCurrentDrawing(prev => ({ ...prev, points: newPoints }));
  }, [isDrawing, currentDrawing]);

  const endDrawing = useCallback(() => {
    if (!isDrawing || !currentDrawing || currentDrawing.points.length < 2) {
      setIsDrawing(false);
      setCurrentDrawing(null);
      setDrawingMetrics({ speed: 0, curvature: 0, confidence: 0 });
      return;
    }
    
    resetIdleTimer();
    const completedLine = { ...currentDrawing, straightness: calculateStraightness(currentDrawing.points), placedAt: Date.now() };
    setDrawnLines(prev => [...prev, completedLine]);
    setTotalPiecesPlaced(prev => prev + 1);
    setIsDrawing(false);
    setCurrentDrawing(null);
    setDrawingMetrics({ speed: 0, curvature: 0, confidence: 0 });
  }, [isDrawing, currentDrawing, calculateStraightness, resetIdleTimer]);

  // --- Force Unknown ---
  const forcedUnknown = useCallback(() => {
    if (phase !== 'building' || forcedUnknownCooldown || isPaused) return;
    resetIdleTimer();
    
    const difficulty = learningRef.current?.difficulty_modifier || 0;
    const radicalChance = 0.1 + difficulty * 0.2;
    const isRadical = Math.random() < radicalChance;
    
    let result, effectName;
    
    if (isRadical && piecesRef.current.length > 3) {
      const idx = Math.floor(Math.random() * RADICAL_EFFECTS.length);
      effectName = `RADICAL: ${RADICAL_NAMES[idx]}`;
      result = RADICAL_EFFECTS[idx](piecesRef.current, difficulty);
      setDrawnLines(prev => applyLineEffects(prev, difficulty, true));
    } else {
      const idx = Math.floor(Math.random() * CHAOS_EFFECTS.length);
      effectName = CHAOS_NAMES[idx];
      result = CHAOS_EFFECTS[idx](piecesRef.current, difficulty);
      setDrawnLines(prev => applyLineEffects(prev, difficulty, false));
    }
    
    setLastChaosEffect(effectName);

    const chaosIds = result.filter(p => p.type === 'chaos' && !piecesRef.current.some(q => q.id === p.id)).map(p => p.id);
    if (chaosIds.length > 0) setTimeout(() => setPieces(prev => prev.filter(p => !chaosIds.includes(p.id))), 4000);

    setPieces(result);
    const confDrop = isRadical ? (8 + Math.random() * 10) : (5 + Math.random() * 7);
    setConfidence(prev => Math.max(0, prev - confDrop));
    setVoidPermanentPenalty(prev => prev + (isRadical ? 4 : 2));

    setForcedUnknownCooldown(true);
    setTimeout(() => setForcedUnknownCooldown(false), 4000);
  }, [phase, forcedUnknownCooldown, resetIdleTimer, isPaused]);

  // --- Game Control ---
  const startGame = useCallback(() => {
    setPhase('building');
    startTimeRef.current = Date.now();
    setDrawnLines([]);
    const dna = (learningRef.current?.fundamental_form_dna || []).filter(d => d.x != null);
    if (dna.length > 0) {
      const startPieces = dna.map((p, i) => ({ id: `dna_${Date.now()}_${i}`, type: p.type || 'cube', x: p.x, y: p.y, placedAt: Date.now() - 1000 + i, isRemnant: true }));
      setPieces(startPieces);
      setFundamentalForm(startPieces);
      recalcConfidence(startPieces);
    }
  }, [recalcConfidence]);

  const getSessionData = useCallback(() => ({
    pieces: piecesRef.current.map(p => ({ type: p.type, x: p.x, y: p.y })),
    max_confidence: maxConfidence,
    rounds_survived: roundsInSession,
    duration_ms: Date.now() - startTimeRef.current,
    fundamental_form: fundamentalForm.map(p => ({ type: p.type, x: p.x, y: p.y })),
    pattern_scores: { density: calcDensity(piecesRef.current), symmetry: calcSymmetry(piecesRef.current), linearity: calcLinearity(piecesRef.current), regularity: calcRegularity(piecesRef.current) },
    equilibrium_reached: equilibriumReached,
  }), [maxConfidence, roundsInSession, fundamentalForm, equilibriumReached]);

  const resetGame = useCallback(() => {
    setPieces([]); setConfidence(0); setPhase('idle'); setRoundsInSession(0);
    setTotalPiecesPlaced(0); setFundamentalForm([]); setDestroyingPieces([]);
    setMaxConfidence(0); setForcedUnknownCooldown(false); setLastChaosEffect('');
    setEquilibriumReached(false); setEquilibriumFading(false);
    setMeasurementStopped(false); setTimeInPorousZone(0);
    setAtmosphericZone('uncertain');
    setUnknownPressure(0); setIdleInducedPressure(0); setGameStartTime(null);
    setVoidPermanentPenalty(0); setWonShapesAvailable({});
    setLotteryWonShape(null); setLotteryWonCount(0); setShowEquilibriumPopup(false);
    porousZoneStartRef.current = null;
    if (entropyTimerRef.current) clearTimeout(entropyTimerRef.current);
    if (unknownTimerRef.current) clearInterval(unknownTimerRef.current);
    startTimeRef.current = Date.now();
  }, []);

  const patternScores = useMemo(() => {
    const allPoints = getAllPoints(pieces, drawnLines);
    if (allPoints.length < 2) return { density: 0, symmetry: 0, linearity: 0, regularity: 0 };
    return {
      density: Math.round(calcDensity(pieces, drawnLines) * 100),
      symmetry: Math.round(calcSymmetry(pieces, drawnLines) * 100),
      linearity: Math.round(calcLinearity(pieces, drawnLines) * 100),
      regularity: Math.round(calcRegularity(pieces, drawnLines) * 100),
    };
  }, [pieces, drawnLines]);

  const archiveStructure = useCallback(() => {
    const timestamp = new Date().toISOString().split('T')[0];
    return {
      title: equilibriumReached ? `Open Structure — ${timestamp}` : `Structure ${roundsInSession + 1} — ${timestamp}`,
      pieces: piecesRef.current.map(p => ({ type: p.type, x: p.x, y: p.y })),
      maxConfidence, equilibriumReached, piecesPlaced: totalPiecesPlaced, wonShape: lotteryWonShape,
    };
  }, [equilibriumReached, roundsInSession, maxConfidence, totalPiecesPlaced, lotteryWonShape]);

  const continueAfterEquilibrium = useCallback(() => {
    setShowEquilibriumPopup(false);
    setRoundsInSession(prev => prev + 1);
    setEquilibriumReached(false); setEquilibriumFading(false);
    setMeasurementStopped(false); setForcedUnknownCooldown(false);
    setLotteryWonShape(null); setLotteryWonCount(0); setTimeInPorousZone(0);
    porousZoneStartRef.current = null;
    setUnknownPressure(0); setIdleInducedPressure(0); setVoidPermanentPenalty(0);
    setUnknownDroppingFast(true);
    lastActivityRef.current = Date.now();
  }, []);

  const endAfterEquilibrium = useCallback(() => setShowEquilibriumPopup(false), []);

  const totalUnknownPressure = unknownPressure + idleInducedPressure + voidPermanentPenalty;
  
  return {
    pieces, confidence, selectedPiece, setSelectedPiece, phase,
    roundsInSession, totalPiecesPlaced, fundamentalForm, destroyingPieces,
    forcedUnknownCooldown, maxConfidence, lastChaosEffect, patternScores,
    atmosphericZone, equilibriumReached, equilibriumFading, 
    measurementStopped, timeInPorousZone, showEquilibriumPopup,
    unknownPressure: totalUnknownPressure, idleTime, voidPermanentPenalty,
    lotteryActive, lotteryCurrentShape, lotteryWonShape, lotteryWonCount, wonShapesAvailable,
    setWonShapesAvailable,
    selectedColor, setSelectedColor: handleSelectColor, colorPreferences, setColorPreferences,
    setSelectedPiece: handleSelectPiece,
    drawnLines, currentDrawing, isDrawing, drawingMetrics,
    startDrawing, continueDrawing, endDrawing,
    handleRotateTool,
    isErasing, isNearLine, startErasing, continueErasing, endErasing,
    placePiece, forcedUnknown, startGame, getSessionData, resetGame, archiveStructure,
    continueAfterEquilibrium, endAfterEquilibrium,
  };
}
